import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { WORD_API_URL } from '../config';
import { profilePath, rememberProfileExit } from '../utils/profileNav';
import AppShell from './AppShell';
import { BadgeIcon, BadgeIconMedal, badgeLabel } from './BadgeIcons';
import { GobletIcon } from './PubHallButton';
import NominationInfo, { WORD_NOMINATION_HELP } from './NominationInfo';
import './HallOfFame.css';

function formatWeekLabel(weekStart) {
  if (!weekStart) return '';
  const start = new Date(`${weekStart}T12:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const sameYear = start.getFullYear() === end.getFullYear();
  const fmt = (d, withYear) =>
    d.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {})
    });
  // Always show year so old demo weeks (e.g. 2025) don't look like the future.
  return `${fmt(start, !sameYear)} — ${fmt(end, true)}`;
}

function placeLabel(place) {
  if (place === 1) return '1-е';
  if (place === 2) return '2-е';
  if (place === 3) return '3-е';
  return place != null ? `${place}` : '';
}

function ScorePodium({ entries, onOpen }) {
  const list = (entries || []).slice(0, 3);
  const byPlace = new Map(list.map((entry) => [Number(entry.place), entry]));
  const ordered = [2, 1, 3].every((place) => byPlace.has(place))
    ? [byPlace.get(2), byPlace.get(1), byPlace.get(3)]
    : [list[1], list[0], list[2]];

  return (
    <div className="hall-podium">
      {ordered.map((entry, slot) => {
        const place = [2, 1, 3][slot];
        if (!entry) return <div key={place} className="hall-podium__slot" />;
        const shownPlace = Number(entry.place) > 0 ? Number(entry.place) : place;
        return (
          <button
            key={entry.userId || entry.username}
            type="button"
            className={`hall-podium__card hall-podium__card--${shownPlace}`}
            onClick={() => onOpen(entry.username)}
          >
            <BadgeIconMedal place={shownPlace} className="hall-podium__medal" />
            <span className="hall-podium__name">{entry.username}</span>
            <span className="hall-podium__meta">
              {entry.meta?.totalScore != null ? `${entry.meta.totalScore} очк.` : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function RankedRow({ entry, meta, onOpen }) {
  const place = Number(entry.place) > 0 ? Number(entry.place) : 1;
  return (
    <button type="button" className="hall-user" onClick={onOpen}>
      <BadgeIconMedal place={place} className="hall-medal" />
      <span className="hall-place">{placeLabel(place)}</span>
      <span className="hall-name">{entry.username}</span>
      {meta}
    </button>
  );
}

function HallOfFame() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cutover, setCutover] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${WORD_API_URL}/awards/hall`);
        if (!cancelled) {
          setWeeks(response.data.weeks || []);
          setCutover(response.data.cutover || null);
        }
      } catch (err) {
        console.error('Hall of fame load failed:', err);
        if (!cancelled) setWeeks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cutoverLabel =
    cutover != null
      ? `${String(cutover.hour).padStart(2, '0')}:${String(cutover.minute).padStart(2, '0')}`
      : '11:00';

  const openProfile = (username) => {
    rememberProfileExit(location.pathname);
    navigate(profilePath(username, 'word'));
  };

  return (
    <AppShell>
      <div className="hall-content">
        <div className="hall-hero">
          <GobletIcon className="hall-hero-goblet" />
          <div className="hall-hero-text">
            <h1 className="hall-page-title">Знаменитости Паба</h1>
            <p className="hall-hero-sub">Победители Недели</p>
          </div>
        </div>

        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : weeks.length === 0 ? (
          <div className="hall-empty">
            Пока нет закрытых недель. Сыграйте неделю — и здесь появятся чемпионы паба.
          </div>
        ) : (
          weeks.map((week) => (
            <section key={week.weekStart} className="hall-week panel">
              <h2 className="hall-week-title">Неделя {formatWeekLabel(week.weekStart)}</h2>

              <div className="hall-block">
                <div className="hall-block-head">
                  <BadgeIcon category="podium" className="badge-icon--lg" />
                  <h3>{badgeLabel('podium')}</h3>
                  <NominationInfo text={WORD_NOMINATION_HELP.podium} />
                </div>
                {week.podium?.length ? (
                  <ScorePodium entries={week.podium} onOpen={openProfile} />
                ) : (
                  <p className="hall-none">Нет данных</p>
                )}
              </div>

              <div className="hall-block">
                <div className="hall-block-head">
                  <BadgeIcon category="fastest" className="badge-icon--lg" />
                  <h3>{badgeLabel('fastest')}</h3>
                  <NominationInfo text={WORD_NOMINATION_HELP.fastest} />
                </div>
                {week.fastest?.length ? (
                  <ul className="hall-list">
                    {week.fastest.map((entry) => (
                      <li key={`f-${entry.userId}`}>
                        <RankedRow
                          entry={entry}
                          onOpen={() => openProfile(entry.username)}
                          meta={
                            entry.meta?.timeSeconds != null ? (
                              <span className="hall-meta">{entry.meta.timeSeconds}с</span>
                            ) : null
                          }
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hall-none">Нет данных</p>
                )}
              </div>

              <div className="hall-block">
                <div className="hall-block-head">
                  <BadgeIcon category="perfect" className="badge-icon--lg" />
                  <h3>{badgeLabel('perfect')}</h3>
                  <NominationInfo text={WORD_NOMINATION_HELP.perfect} />
                </div>
                {week.perfect?.length ? (
                  <ul className="hall-list hall-list--wrap">
                    {week.perfect.map((entry) => (
                      <li key={`pf-${entry.userId}`}>
                        <button
                          type="button"
                          className="hall-user hall-user--chip"
                          onClick={() => openProfile(entry.username)}
                        >
                          {entry.username}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hall-none">Никто не закрыл все слова</p>
                )}
              </div>
            </section>
          ))
        )}
        <p className="hall-intro">
          Итоги недели публикуются в понедельник в {cutoverLabel} (МСК) — вместе с новым словом.
        </p>
      </div>
    </AppShell>
  );
}

export default HallOfFame;
